import { useTranslation } from 'react-i18next';
import { Card } from '../ui/Card';
import type { AxisScores } from '../../types/api.js';

interface AxisChartProps {
  /** 5軸スコア */
  axes: AxisScores;
}

/** @graph-connects none */
interface AxisConfig {
  key: keyof AxisScores;
  labelKey: string;
  weight: number;
}

/** @graph-connects none */
const AXIS_CONFIGS: AxisConfig[] = [
  { key: 'a1', labelKey: 'scoring.axis.a1', weight: 30 },
  { key: 'a2', labelKey: 'scoring.axis.a2', weight: 25 },
  { key: 'a3', labelKey: 'scoring.axis.a3', weight: 20 },
  { key: 'a4', labelKey: 'scoring.axis.a4', weight: 15 },
  { key: 'a5', labelKey: 'scoring.axis.a5', weight: 10 },
];

/** @graph-connects none */
function getScoreColor(score: number): string {
  if (score >= 80) return 'var(--color-score-high)';
  if (score >= 60) return 'var(--color-score-good)';
  if (score >= 40) return 'var(--color-score-mid)';
  if (score >= 20) return 'var(--color-score-low)';
  return 'var(--color-score-bad)';
}

/**
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business 5軸水平バーチャート。各軸のラベル・ウェイト・スコアを色分けバーで表示
 * @graph-connects none
 */
export function AxisChart({ axes }: AxisChartProps) {
  const { t } = useTranslation();

  return (
    <Card variant="glass">
      <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4">
        {t('scoring.axisTitle')}
      </h3>
      <div className="space-y-3">
        {AXIS_CONFIGS.map((cfg) => {
          const score = axes[cfg.key];
          return (
            <div key={cfg.key}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-[var(--color-text-secondary)]">
                  {t(cfg.labelKey)}
                  <span className="text-[var(--color-text-muted)] ml-1">({cfg.weight}%)</span>
                </span>
                <span className="text-xs font-mono font-bold text-[var(--color-text-primary)]">
                  {score}
                </span>
              </div>
              <div className="score-bar">
                <div
                  className="score-bar-fill"
                  style={{
                    width: `${String(Math.min(score, 100))}%`,
                    backgroundColor: getScoreColor(score),
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
